import React, { useState } from 'react';
import tw from 'twin.macro';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { EyeIcon as Eye } from '@heroicons/react/24/outline';
import { EyeSlashIcon as EyeOff } from '@heroicons/react/24/outline';
import ButtonGroup from '@teetsh/app/src/components/Form/ButtonGroup';
import { ArrowUpIcon as ArrowUp } from '@heroicons/react/20/solid';
import swap from '@teetsh/app/src/utils/arrays/swap';
import { ArrowDownIcon as ArrowDown } from '@heroicons/react/20/solid';
import { Column } from '@tanstack/react-table';
import { CustomFieldDefinition } from '@teetsh/app/src/types/customField';
import { ArrowLeftIcon as ArrowLeft } from '@heroicons/react/24/outline';
import { CustomFieldForm } from 'components/CustomFieldForm/CustomFieldForm';
import useUpdateElevesCustomFields from '@teetsh/app/src/queries/eleves/customFields/useUpdateElevesCustomFields';
import { AddNewField } from '@teetsh/app/src/containers/Eleves/List/components/AddNewField';
import { PlusIcon as Plus } from '@heroicons/react/24/outline';
import useDeleteElevesCustomField from 'queries/eleves/customFields/useDeleteElevesCustomField';
import { useTranslation } from 'react-i18next';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';

interface Props {
  columns: Column<any, any>[];
  viewId: string;
  onColumnOrderChange: (v: string[]) => void;
  customFields: CustomFieldDefinition[];
}

export function ColumnsVisibility(props: Props) {
  const { columns, onColumnOrderChange, viewId } = props;
  const classrooms = useClassrooms();
  const [editCustomField, setEditCustomField] =
    useState<CustomFieldDefinition | null>(null);
  const deleteCustomField = useDeleteElevesCustomField();
  const { t } = useTranslation(['eleve']);
  const [newCustomFieldOpen, setNewCustomFieldOpen] = useState(false);
  const updateCustomField = useUpdateElevesCustomFields();
  const visibleColumns = columns.filter(c => c.getIsVisible());
  const hiddenColumns = columns.filter(c => !c.getIsVisible());

  if (newCustomFieldOpen) {
    return (
      <div tw="px-4">
        <div tw="flex space-x-4 mb-4 items-center">
          <Button
            icon={ArrowLeft}
            onClick={() => setNewCustomFieldOpen(false)}
            minimal
          />
          <div tw="text-sm">{t('eleve:actions.creatNewColumn')}</div>
        </div>
        <div tw="max-h-[60vh] overflow-y-auto">
          <AddNewField
            viewId={viewId}
            onClose={() => setNewCustomFieldOpen(false)}
          />
        </div>
      </div>
    );
  }
  if (editCustomField) {
    return (
      <div tw="px-4">
        <div tw="flex space-x-4 mb-4 items-center">
          <Button
            icon={ArrowLeft}
            onClick={() => setEditCustomField(null)}
            minimal
          />
          <div tw="text-sm">{t('eleve:actions.modifyField')}</div>
        </div>
        <CustomFieldForm
          onClose={() => setEditCustomField(null)}
          onSubmit={v =>
            updateCustomField({
              id: editCustomField.id,
              ...v,
            })
          }
          cycles={classrooms.flatMap(c => c.cycle)}
          availableFieldTypes={[
            'text',
            'number',
            'phone',
            'multiselect',
            'filesAndMedia',
            'bool',
            'select',
          ]}
          onDelete={id => deleteCustomField({ customFieldId: id })}
          customFieldId={editCustomField.id}
          initialValues={editCustomField}
        />
      </div>
    );
  }
  return (
    <div>
      <div tw="overflow-auto px-4 max-h-[50vh]">
        <div tw="text-xs text-muted mb-1">{t('eleve:displayedColumns')}</div>
        <div tw="mb-4 space-y-2">
          {visibleColumns.map((c, index) => {
            const columnName =
              (c?.columnDef?.header && (c.columnDef.header as any)()) || '';
            const matchingCustomField = props.customFields.find(
              f => f.id === c.id,
            );
            return (
              <div
                css={[
                  !c.getIsVisible() && tw`text-muted!`,
                  tw`flex items-center`,
                ]}
                key={c.id}
              >
                <Button
                  onClick={() => c.toggleVisibility(!c.getIsVisible())}
                  small
                  minimal
                  aria-label={`${
                    c.getIsVisible() ? 'Cacher' : 'Montrer'
                  } ${columnName}`}
                  icon={c.getIsVisible() ? Eye : EyeOff}
                />
                <div
                  onClick={() =>
                    matchingCustomField &&
                    setEditCustomField(matchingCustomField)
                  }
                  css={[
                    matchingCustomField &&
                      tw`hover:underline cursor-pointer italic`,
                    tw`ml-2 flex-grow text-sm`,
                  ]}
                  role="presentation"
                >
                  {columnName}
                </div>
                <div>
                  <ButtonGroup>
                    <Button
                      small
                      minimal
                      icon={ArrowUp}
                      onClick={() =>
                        onColumnOrderChange(
                          swap(
                            columns.map(cc => cc.id),
                            index,
                            -1,
                          ),
                        )
                      }
                    />
                    <Button
                      small
                      minimal
                      icon={ArrowDown}
                      onClick={() =>
                        onColumnOrderChange(
                          swap(
                            columns.map(cc => cc.id),
                            index,
                            1,
                          ),
                        )
                      }
                    />
                  </ButtonGroup>
                </div>
              </div>
            );
          })}
        </div>
        {hiddenColumns.length > 0 && (
          <div>
            <div tw="text-xs text-muted mb-1">{t('eleve:hiddenColumns')}</div>
            <div tw="space-y-1">
              {hiddenColumns.map(c => {
                const columnName =
                  (c?.columnDef?.header && (c.columnDef.header as any)()) || '';
                const matchingCustomField = props.customFields.find(
                  f => f.id === c.id,
                );
                return (
                  <div
                    css={[
                      !c.getIsVisible() && tw`text-muted!`,
                      tw`flex items-center`,
                    ]}
                    key={c.id}
                  >
                    <Button
                      onClick={() => c.toggleVisibility(!c.getIsVisible())}
                      small
                      minimal
                      aria-label={`${
                        c.getIsVisible() ? 'Cacher' : 'Montrer'
                      } ${columnName}`}
                      icon={c.getIsVisible() ? Eye : EyeOff}
                    />
                    <div
                      onClick={() =>
                        matchingCustomField &&
                        setEditCustomField(matchingCustomField)
                      }
                      css={[
                        matchingCustomField &&
                          tw`hover:underline cursor-pointer italic`,
                        tw`ml-2 flex-grow text-sm`,
                      ]}
                    >
                      {columnName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div tw="px-4 pt-4 border-t">
        <Button
          icon={Plus}
          small
          fill
          onClick={() => setNewCustomFieldOpen(true)}
        >
          {t('eleve:actions.addColumn')}
        </Button>
      </div>
    </div>
  );
}
